"use client";
import { Track } from "@/src/lib/tracking";

import { Icon, OspToast, ospThemes } from "@ospweb/sdk-ui";
import React, { useCallback } from "react";
import { cn, pubsub } from "utils";
import { EdgenModalCreateAble } from "../../EdgenModal";
import type {
  RedeemCodeModalCreateAbleProps,
  RedeemCodeModalProps,
  RedeemCodeModalResult,
} from "./types";

import { I18nUtil } from "@utils/i18n";
import { Reward } from "@/src/hooks/useQueryRewards";
import Translations from "@translate/components/modal/RedeemCode/RedeemCodeModal";
import { useRedeemCodeIntegration } from "@/src/hooks/useRedeemCodeIntegration";
import { useRedeemCodeState } from "@/src/hooks/useRedeemCodeState";
import { pubSubKeyMap } from "@utils/pubSubKeyMap";

const I18n = I18nUtil.createScoped(Translations);

/**
 * 兑换码弹窗组件
 * 基于Figma设计稿实现：待输入状态、格式错误状态、已使用状态
 */
const RedeemCodeModal: React.FC<RedeemCodeModalProps> = ({
  onClose,
  onSuccess,
  code: initCode,
  source = "manual",
}) => {
  const {
    inputCode,
    isRedeeming,
    error,
    canRedeem,
    hasError,
    handleInputChange,
    handleRedeem,
    resetState,
  } = useRedeemCodeState(initCode, source);

  const integration = useRedeemCodeIntegration();

  const handleRedeemSuccessCallback = useCallback(
    async (rewards: Reward[]) => {
      try {
        if (onSuccess) {
          onSuccess(rewards);
          resetState();
        }
        setTimeout(async () => {
          await integration.triggerRewardPopup(rewards);
        }, 300);
        await integration.refreshRewards();
      } catch (error) {
        Track.error(error, {
          tag: "RedeemCodeModal",
          msg: "Failed to handle redeem success:",
        });

        OspToast.error({
          content: "Failed to process rewards. Please try again.",
        });
        onSuccess?.(rewards);
        resetState();
      }
    },
    [onSuccess, resetState, integration],
  );

  const handleRedeemClick = useCallback(async () => {
    try {
      const result = await handleRedeem();
      if (result && result.success) {
        pubsub.emit(pubSubKeyMap.MEMBER_PURCHASE_SUCCESS);
        await handleRedeemSuccessCallback(result.rewards);
      }
    } catch (err) {
      Track.error(err, { tag: "RedeemCodeModal", msg: "Redeem error:" });
    }
  }, [handleRedeem, handleRedeemSuccessCallback]);

  const handleInputChangeWrapper = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      handleInputChange(e.target.value);
    },
    [handleInputChange],
  );

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && canRedeem) {
        handleRedeemClick();
      }
    },
    [canRedeem, handleRedeemClick],
  );

  const getErrorMessage = (): string | null => {
    if (!error) {
      return null;
    }

    if (error.includes("Invalid Code Format")) {
      return "Invalid Code Format.";
    } else if (error.includes("Already Redeemed")) {
      return "Code Already Redeemed.";
    } else if (error.includes("not found")) {
      return "Code not found.";
    } else if (error.includes("Failed to issue reward")) {
      return "Failed to issue reward. Retry.";
    }

    return error;
  };

  const errorMessage = getErrorMessage();

  return (
    <div
      data-or-view="member_redeem_modal_view"
      data-or-param-source={source}
      className="relative flex w-full flex-col items-start gap-6 overflow-hidden p-6 bg-background_solid_secondary rounded-2xl"
    >
      <button
        data-openreplay-label="common_redeemcodemodal_close"
        id="btn-close-redeemcode-redeemcodemodal-12"
        onClick={onClose}
        className="absolute right-3 top-3 w-7 h-7 rounded-full flex items-center justify-center transition-colors"
        aria-label="Close"
      >
        <Icon
          name="close"
          size={16}
          className="text-content_tertiary hover:text-content_secondary"
        />
      </button>
      <div className="flex flex-col gap-2 w-full">
        <h2
          aria-label="Redeem Code Modal h2"
          data-openreplay-label="common_redeemcodemodal_h2"
          id="sem-redeem-code-modal-h2"
          className="headline_regular_s text-content_primary"
        >
          {I18n.t("Redeem Rewards")}
        </h2>
        <p className="body_m text-content_secondary">
          {I18n.t(
            "Enter your code to claim exclusive membership benefits and Aura rewards",
          )}
        </p>
      </div>
      <div className="flex flex-col gap-2 w-full">
        <div
          className={cn(
            "relative flex items-center h-11 px-3 rounded-lg border transition-colors",
            "bg-background_white_overlay5",
            {
              "border-border_accent2_solid": hasError,
              "border-border_white_transparent10": !hasError,
              "focus-within:border-content_brand_primary": !hasError,
            },
          )}
        >
          <input
            type="text"
            value={inputCode}
            onChange={handleInputChangeWrapper}
            maxLength={100}
            onKeyPress={handleKeyPress}
            placeholder={I18n.t("Enter your code")}
            className={cn(
              "flex-1 bg-transparent outline-none",
              "body_medium_s text-content_primary placeholder:text-content_tertiary",
              "disabled:opacity-50",
            )}
            disabled={isRedeeming}
            autoFocus
          />
        </div>

        {errorMessage && (
          <div className="flex items-center gap-1 px-1">
            <Icon
              name="alert-circle"
              size={12}
              className="text-content_accent2_primary"
            />
            <span className="body_s text-content_accent2_primary">
              {errorMessage}
            </span>
          </div>
        )}
      </div>
      <button
        aria-label="Redeem Code Modal"
        data-openreplay-label="common_redeemcodemodal_click"
        id="btn-redeem-code-modal"
        onClick={handleRedeemClick}
        className={cn(
          "button primary_button md_button w-full",
          "bg-background_white text-content_inverse_primary",
          "button_m group h-11",
          "disabled:opacity-50 disabled:cursor-not-allowed",
        )}
        disabled={!canRedeem}
        data-loading={isRedeeming}
      >
        <span className="group-loading:hidden">
          {isRedeeming ? "Redeeming..." : "Redeem Rewards"}
        </span>
        <Icon
          name="ic_loading2"
          size={20}
          color={ospThemes.colors.content_inverse_primary}
          className={cn("hidden animate-spin group-loading:block")}
        />
      </button>
    </div>
  );
};

export const RedeemCodeModalCreateAble = async (
  props: RedeemCodeModalCreateAbleProps,
): Promise<RedeemCodeModalResult> => {
  const { modalProps, code, source } = props;

  return new Promise((resolve) => {
    EdgenModalCreateAble.call({
      children: (
        <RedeemCodeModal
          onClose={() => {
            EdgenModalCreateAble.end(false);
            resolve({
              action: "close",
            });
          }}
          code={code}
          source={source}
          onSuccess={(rewards) => {
            EdgenModalCreateAble.end(false);
            resolve({
              action: "success",
              rewards,
            });
          }}
        />
      ),
      containerClassName: "!md:w-[400px] !md:rounded-2xl",
      className: "[--modal-radius:16px_16px_0_0] md:[--modal-radius:16px]",
      contentStyle: {
        borderRadius: "var(--modal-radius)",
        backgroundColor: ospThemes.colors.background_solid_secondary,
        width: "100%",
        padding: 0,
      },
      titleStyle: {
        minHeight: 0,
      },
      showClose: false,
      destroyOnClose: true,
      ...modalProps,
    });
  });
};
